import { app } from '@microsoft/teams-js';
import memoize from 'memoizee';
import { Logger } from "../../packages/logger/src";

declare global {
    interface Window {
        mSTeamsContext: any;
    }
  }
/**
 * Returns the MS Teams context object, ensuring that the app has been initialized first
 *
 * @returns {Promise<app.Context>} MS Teams context promise
 */
export const getMSTeamsContext = memoize(async () => {
    if ( window.mSTeamsContext ) {
        return window.mSTeamsContext; 
    }
    await app.initialize();
    window.mSTeamsContext = app.getContext();
    return window.mSTeamsContext;
}, {});

/**
 * Loads the localization language from the microsoft teams context
 *
 * @returns {Promise<string>} localization language code
 */
export async function getLocalizationLanguage(): Promise<string> {
  const context = await getMSTeamsContext().catch((error) => {
    Logger.error(error);
  });
  return context.app.locale;
}

/**
 * Loads the theme from the microsoft teams context
 *
 * @returns {Promise<string>} teams theme string
 */
export async function getMSTeamsTheme(): Promise<string> {
  const context = await getMSTeamsContext().catch((error) => {
    Logger.error(error);
  });
  return context.app.theme;
}

export function processArray(originalArray: string[]): [string[], string[]] {
  const array1: string[] = [];
  const array2: string[] = [];

  let foundComma = false;
  let wordsAfterComma: string[] = [];

  for (const element of originalArray) {
    if (element.startsWith(',')) {
      foundComma = true;
      wordsAfterComma = element.substring(1).split(' ');
    } else if (!foundComma) {
      array1.push(element);
    } else {
      wordsAfterComma.push(element);
    }
  }
  return [array1, wordsAfterComma];
}

export function extractTextBetweenDoubleDashes(inputString: any) {
  // Safety check: ensure inputString is valid
  if (!inputString || typeof inputString !== 'string') {
    Logger.info(`SD: MsTeamsContextUtil.extractTextBetweenDoubleDashes - Invalid input: ${inputString}`);
    return "";
  }

  try {
    // For speed dial format: {callType}--{contactId}--{phoneType}
    // We need to extract contactId even if it contains '--' characters
    // 
    // Strategy: Find first '--' (after 2-char callType) and last '--' (before 2-char phoneType)
    // This handles contact IDs that contain '--' internally
    
    const firstDashIndex = inputString.indexOf('--');
    if (firstDashIndex === -1) {
      Logger.info(`SD: extractTextBetweenDoubleDashes - No first -- found in: ${inputString}`);
      return "";
    }
    
    const lastDashIndex = inputString.lastIndexOf('--');
    if (lastDashIndex === -1 || lastDashIndex === firstDashIndex) {
      Logger.info(`SD: MsTeamsContextUtil.extractTextBetweenDoubleDashes - No last -- found in: ${inputString}`);
      return "";
    }
    
    // Extract the contact ID between first and last '--'
    const contactId = inputString.substring(firstDashIndex + 2, lastDashIndex);
    
    if (contactId.length === 0) {
      Logger.info(`SD: MsTeamsContextUtil.extractTextBetweenDoubleDashes - Empty contact ID extracted from: ${inputString}`);
      return "";
    }
    // Reducing noise - uncomment for debugging if needed
    // console.log(`SD: MsTeamsContextUtil.extractTextBetweenDoubleDashes - Contact ID extracted from: ${inputString}, Contact ID: ${contactId}`);
    return contactId;
  } catch (error) {
    Logger.error(`SD: MsTeamsContextUtil.extractTextBetweenDoubleDashes - Error processing input: ${JSON.stringify(error)}, input: ${inputString}`);
    return "";
  }
}
